import { FORMATION_ORDER } from "../config/constants.ts";
import { GESTURE_NEUTRAL_MS } from "../particles/visualBounds.ts";
import { AudioController } from "../audio/AudioController.ts";
import { CameraController, describeCameraError } from "../camera/CameraController.ts";
import { DemoSequence } from "../demo/DemoSequence.ts";
import { VisualTestMode } from "../demo/VisualTestMode.ts";
import { GestureRecognizer } from "../gestures/GestureRecognizer.ts";
import { ParticleUniverse, isWebGLAvailable } from "../particles/ParticleUniverse.ts";
import { PerformanceMonitor } from "../performance/PerformanceMonitor.ts";
import { FacePrivacyProcessor } from "../privacy/FacePrivacyProcessor.ts";
import { canRecordFromPrivacy, expandFaceBox, mirrorRect } from "../privacy/privacyMath.ts";
import { RecordingCompositor } from "../recording/RecordingCompositor.ts";
import { loadLook, saveLook, validateLook, type VisualLook } from "../settings/look.ts";
import { SettingsStore } from "../settings/SettingsStore.ts";
import { FaceTracker } from "../tracking/FaceTracker.ts";
import { HandTracker } from "../tracking/HandTracker.ts";
import { drawFallbackBanner, drawHandOverlay } from "../ui/HandOverlay.ts";
import { UIController } from "../ui/UIController.ts";
import type { AppSettings, FormationId } from "../utils/types.ts";

export class App {
  private ui = new UIController();
  private settings = new SettingsStore();
  private camera = new CameraController();
  private privacy = new FacePrivacyProcessor();
  private hands = new HandTracker();
  private faces = new FaceTracker();
  private audio = new AudioController();
  private demo = new DemoSequence();
  private visualTest = new VisualTestMode();
  private look: VisualLook = loadLook();
  private lastFlashed = "";
  private lastVisualStep = "";
  private lastVisualFormationAt = 0;
  private perf = new PerformanceMonitor();
  private universe: ParticleUniverse;
  private recorder: RecordingCompositor;
  private gesture = new GestureRecognizer();
  private raf = 0;
  private lastT = 0;
  private detectorsReady = false;
  private cameraOn = false;

  private blackHoleLatched = false;
  private lastDrivingAt = 0;
  private lastGestureId = "none";
  private timedRec: number | null = null;
  private previewCtx: CanvasRenderingContext2D;
  private disposed = false;

  constructor() {
    const canvas = document.getElementById("universe-canvas");
    const recordCanvas = document.getElementById("record-compositor");
    if (!(canvas instanceof HTMLCanvasElement) || !(recordCanvas instanceof HTMLCanvasElement)) {
      throw new Error("Required canvases are missing.");
    }
    const probe = document.createElement("canvas");
    if (!isWebGLAvailable(probe)) {
      throw new Error("WebGL is unavailable, so the particle universe cannot start.");
    }
    this.universe = new ParticleUniverse(canvas);
    this.recorder = new RecordingCompositor(recordCanvas);
    const preview = this.ui.cameraCanvas.getContext("2d");
    if (!preview) throw new Error("Processed camera canvas is unavailable.");
    this.previewCtx = preview;
  }

  start(): void {
    this.bindUi();
    this.applySettings(this.settings.get());
    this.universe.applyLook(this.look);
    this.syncLookForm();
    this.ui.fadeTitle();
    this.resize();
    this.lastT = performance.now();
    this.raf = requestAnimationFrame(this.tick);
    this.camera.onDisconnected = () => {
      this.shutdownCamera(
        "The camera was disconnected. Privacy fallback is active and Demo Mode continues.",
      );
    };
    window.addEventListener("resize", this.resize);
    window.addEventListener("orientationchange", this.resize);
    window.addEventListener("keydown", this.onKey);
    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("pagehide", this.dispose);
    window.addEventListener("beforeunload", this.dispose);
  }

  private bindUi(): void {
    this.ui.landingEnable.addEventListener("click", () => void this.enableCamera());
    this.ui.btnEnable.addEventListener("click", () => void this.enableCamera());
    this.ui.landingDemo.addEventListener("click", () => this.enterDemo());
    this.ui.btnCameraOff.addEventListener("click", () => this.shutdownCamera());
    this.ui.errorDismiss.addEventListener("click", () => this.ui.hideError());
    this.ui.btnSettings.addEventListener("click", () => this.ui.toggleSettings());
    this.ui.settingsClose.addEventListener("click", () => this.ui.toggleSettings(false));
    this.ui.cameraCollapse.addEventListener("click", () => this.ui.toggleCameraCollapse());
    this.ui.btnPresentation.addEventListener("click", () =>
      this.settings.set({ presentationMode: !this.settings.get().presentationMode }),
    );
    this.ui.btnSound.addEventListener("click", () => void this.toggleSound());
    this.ui.volumeSlider.addEventListener("input", () => {
      const volume = Number(this.ui.volumeSlider.value);
      this.settings.set({ volume });
      this.audio.setVolume(volume);
    });
    this.ui.btnRecord.addEventListener("click", () => void this.toggleRecording());
    this.ui.btnRecord15.addEventListener("click", () => void this.timedRecording(15));
    this.ui.btnRecord30.addEventListener("click", () => void this.timedRecording(30));
    this.ui.btnDownload.addEventListener("click", () => this.recorder.download());
    this.ui.btnDemo.addEventListener("click", () => this.toggleDemo(15));
    this.ui.btnDemo30.addEventListener("click", () => this.toggleDemo(30));
    this.ui.btnReset.addEventListener("click", () => this.resetVisuals());
    this.ui.btnVisualTest.addEventListener("click", () => this.toggleVisualTest());
    this.ui.btnCalibrate.addEventListener("click", () => this.ui.toggleCalibration());
    this.ui.calibrationClose.addEventListener("click", () => this.ui.toggleCalibration(false));
    this.ui.calibrationForm.addEventListener("input", () => this.readLookForm());
    this.ui.settingsForm.addEventListener("change", () => this.readSettingsForm());
    this.ui.settingsForm.addEventListener("input", () => this.readSettingsForm());
    this.settings.subscribe((value) => this.applySettings(value));

    const canvas = this.universe.canvas;
    canvas.addEventListener("mousemove", this.onMouseMove);
    canvas.addEventListener("mousedown", this.onMouseDown);
    canvas.addEventListener("mouseup", this.onMouseUp);
  }

  private readSettingsForm(): void {
    const form = this.ui.settingsForm;
    const data = new FormData(form);
    this.settings.set({
      privacyMode: String(data.get("privacyMode")) as AppSettings["privacyMode"],
      concealmentType: String(data.get("concealmentType")) as AppSettings["concealmentType"],
      concealmentStrength: Number(data.get("concealmentStrength")),
      cameraPanelSize: String(data.get("cameraPanelSize")) as AppSettings["cameraPanelSize"],
      cameraPanelPosition: String(
        data.get("cameraPanelPosition"),
      ) as AppSettings["cameraPanelPosition"],
      particleQuality: String(data.get("particleQuality")) as AppSettings["particleQuality"],
      colourTheme: String(data.get("colourTheme")) as AppSettings["colourTheme"],
      gestureSensitivity: Number(data.get("gestureSensitivity")),
      gestureSmoothing: Number(data.get("gestureSmoothing")),
      soundEnabled: Boolean(form.querySelector<HTMLInputElement>('[name="soundEnabled"]')?.checked),
      volume: Number(data.get("volume")),
      showFps: Boolean(form.querySelector<HTMLInputElement>('[name="showFps"]')?.checked),
      debugMode: Boolean(form.querySelector<HTMLInputElement>('[name="debugMode"]')?.checked),
      presentationMode: Boolean(
        form.querySelector<HTMLInputElement>('[name="presentationMode"]')?.checked,
      ),
      mirrorCamera: Boolean(form.querySelector<HTMLInputElement>('[name="mirrorCamera"]')?.checked),
    });
  }

  private applySettings(settings: AppSettings): void {
    this.ui.applySettings(settings);
    this.universe.setQuality(settings.particleQuality);
    this.universe.setTheme(settings.colourTheme);
    void this.audio.setEnabled(settings.soundEnabled);
    this.audio.setVolume(settings.volume);
  }

  private enterDemo(): void {
    this.ui.hideLanding();
    this.ui.btnEnable.hidden = false;
    this.ui.setCameraEnabled(false);
    this.ui.setCameraCover(true, "DEMO MODE — CAMERA OFF");
  }

  private async enableCamera(): Promise<void> {
    this.ui.hideLanding();
    this.ui.setCameraCover(true, "INITIALISING PRIVATE CAMERA…");
    this.ui.setCameraEnabled(false);
    try {
      await this.camera.enable();
      if (!this.detectorsReady) {
        try {
          await Promise.all([this.hands.init(), this.faces.init()]);
          this.detectorsReady = true;
        } catch (error) {
          this.camera.stop();
          throw Object.assign(
            new Error(
              "Local hand/face models could not be loaded. The camera was stopped so raw video cannot appear. Check /models and /wasm, then retry.",
            ),
            { cause: error },
          );
        }
      }
      this.cameraOn = true;
      this.privacy.reset();
      this.hands.reset();
      this.gesture.reset();
      this.ui.setCameraEnabled(true);
    } catch (error) {
      this.cameraOn = false;
      this.ui.setCameraEnabled(false);
      this.ui.setCameraCover(true, "CAMERA UNAVAILABLE");
      this.ui.showError(describeCameraError(error));
    }
  }

  private shutdownCamera(message?: string): void {
    this.cameraOn = false;
    this.camera.stop();
    this.privacy.reset();
    this.hands.reset();
    this.gesture.reset();
    const ctx = this.previewCtx;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, this.ui.cameraCanvas.width, this.ui.cameraCanvas.height);
    this.ui.setCameraEnabled(false);
    this.ui.setCameraCover(true, message ?? "CAMERA OFF");
    this.ui.setGesture("NO HANDS DETECTED", 0);
    this.ui.btnEnable.hidden = false;
  }

  private async toggleSound(): Promise<void> {
    const next = !this.settings.get().soundEnabled;
    this.settings.set({ soundEnabled: next });
    await this.audio.setEnabled(next);
  }

  private privacyGate() {
    const state = this.privacy.getState();
    return {
      cameraActive: this.cameraOn,
      concealmentApplied: state.concealmentApplied,
      ready: state.ready && this.detectorsReady,
      unsafe: this.cameraOn && (!state.concealmentApplied || !state.ready),
    };
  }

  private async toggleRecording(): Promise<void> {
    if (this.recorder.recording) {
      await this.finishRecording();
      return;
    }
    const gate = this.privacyGate();
    const allowed = canRecordFromPrivacy(gate);
    if (!allowed.ok) {
      this.ui.showError(allowed.reason ?? "Recording blocked.");
      return;
    }
    try {
      await this.recorder.start({
        gate,
        audioStream: this.audio.captureStream(),
        onCountdown: (n) => this.ui.setCountdown(n),
      });
      this.ui.setRecording(true, 0);
    } catch (error) {
      this.ui.showError(error instanceof Error ? error.message : "Recording could not start.");
    }
  }

  private async timedRecording(seconds: number): Promise<void> {
    if (this.recorder.recording) return;
    await this.toggleRecording();
    if (!this.recorder.recording) return;
    this.timedRec = window.setTimeout(() => void this.finishRecording(), seconds * 1000);
  }

  private async finishRecording(): Promise<void> {
    if (this.timedRec) {
      window.clearTimeout(this.timedRec);
      this.timedRec = null;
    }
    const blob = await this.recorder.stop();
    this.ui.setRecording(false);
    this.ui.setCountdown(0);
    this.ui.setDownloadEnabled(Boolean(blob));
    if (blob) {
      this.ui.showError(
        "Recording complete. Download the WebM file locally. Some social platforms need conversion (for example to MP4) before upload. Nothing was uploaded.",
      );
    }
  }

  private tick = (now: number): void => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.tick);
    const dt = Math.min(0.05, (now - this.lastT) / 1000 || 0.016);
    this.lastT = now;
    const settings = this.settings.get();
    const fps = this.perf.update(dt);
    this.universe.adapt(this.perf.shouldDowngrade(), this.perf.shouldUpgrade());

    if (this.cameraOn && this.detectorsReady) {
      this.processCamera(now, settings);
    } else if (!this.cameraOn) {
      this.clearPreviewToBlack();
    }

    if (this.visualTest.isActive()) this.stepVisualTest(now);
    else if (!this.cameraOn && now - this.lastDrivingAt > GESTURE_NEUTRAL_MS) {
      this.universe.settleToBaseline();
    }

    this.universe.update(dt, now / 1000);
    this.universe.render();
    this.composeRecording();
    this.ui.setFps(fps, settings.showFps && !settings.presentationMode);
    this.ui.setDemoCoach(this.demo.current());
    if (this.recorder.recording) this.ui.setRecording(true, this.recorder.elapsedSeconds());
    if (
      settings.debugMode &&
      !settings.presentationMode &&
      !(this.cameraOn && this.detectorsReady)
    ) {
      this.ui.setDebug(this.formatDebug(this.lastGestureId, 0, fpsLine(fps)));
    } else if (!settings.debugMode || settings.presentationMode) {
      this.ui.setDebug(null);
    }
  };

  private processCamera(now: number, settings: AppSettings): void {
    const video = this.camera.getVideo();
    if (!video || video.readyState < 2) {
      this.ui.setCameraCover(true, "INITIALISING PRIVATE CAMERA…");
      return;
    }
    const faces = this.faces.detect(video, now);
    const detectorError = Boolean(this.faces.lastError);
    const privacy = this.privacy.process(video, faces, settings, now, detectorError);
    const hands = this.hands.detect(
      video,
      now + 0.01,
      settings.mirrorCamera,
      settings.gestureSmoothing,
    );
    const processed = this.privacy.getCanvas();
    const out = this.ui.cameraCanvas;
    if (out.width !== processed.width || out.height !== processed.height) {
      out.width = processed.width;
      out.height = processed.height;
    }
    this.previewCtx.drawImage(processed, 0, 0);
    const debugBoxes = faces.map((face) => {
      const expanded = expandFaceBox(face.box, processed.width, processed.height);
      return settings.mirrorCamera ? mirrorRect(expanded, processed.width) : expanded;
    });
    drawHandOverlay(this.previewCtx, hands, out.width, out.height, {
      enabled: settings.debugMode,
      boxes: debugBoxes,
      fallback: privacy.fallbackActive,
    });
    if (privacy.fallbackActive) drawFallbackBanner(this.previewCtx, out.width, out.height);

    this.ui.setCameraCover(false, privacy.message);
    this.ui.setPrivacy(privacy);

    const output = this.gesture.step(hands, now, settings.gestureSensitivity);
    this.ui.setGesture(output.label, output.confidence);
    this.applyGesture(output, now);
    this.lastGestureId = output.id;

    if (settings.debugMode && !settings.presentationMode) {
      this.ui.setDebug(
        this.formatDebug(output.id, output.confidence, fpsLine(this.perf.getFps()), {
          faces: faces.length,
          faceMs: this.faces.lastProcessMs,
          hands: hands.length,
          handMs: this.hands.lastProcessMs,
          fallback: privacy.fallbackActive,
        }),
      );
    }
  }

  private toggleDemo(seconds: 15 | 30): void {
    if (this.demo.isActive()) {
      this.demo.stop();
      return;
    }
    this.demo.start(seconds);
  }

  private applyGesture(output: ReturnType<GestureRecognizer["step"]>, now: number): void {
    const driving =
      output.id === "expand" ||
      output.id === "gravity" ||
      output.id === "cosmic-expansion" ||
      output.blackHoleHeld;
    if (output.noHands) {
      if (now - this.lastDrivingAt > GESTURE_NEUTRAL_MS) this.universe.settleToBaseline();
      if (this.blackHoleLatched) {
        this.universe.setBlackHole(false);
        this.blackHoleLatched = false;
      }
      return;
    }
    if (output.id === "expand") this.universe.awaken();
    if (output.id === "gravity") this.universe.compress(output.pinchAmount);
    if (output.id === "orbit") this.universe.orbit(output.orbitX, output.orbitY);
    if (output.id === "cosmic-expansion") this.universe.expandCosmic(output.expansionAmount);
    if (output.blackHoleHeld) {
      this.universe.setBlackHole(true);
      if (!this.blackHoleLatched) {
        this.audio.blackHole(true);
        this.blackHoleLatched = true;
      }
    } else if (this.blackHoleLatched) {
      this.universe.setBlackHole(false);
      this.blackHoleLatched = false;
    }
    if (output.fireSupernova) {
      this.universe.supernova();
      this.audio.supernova();
    }
    if (output.fireSwipe) {
      const id = this.universe.cycleFormation(output.fireSwipe);
      this.ui.toastFormation(id);
      this.ui.flashGesture(id.replace(/-/g, " ").toUpperCase());
      this.audio.formationSweep();
    }
    if (driving || output.id === "orbit" || output.fireSupernova) {
      const flashId = output.fireSupernova ? "SUPERNOVA" : output.label;
      if (flashId && flashId !== this.lastFlashed && flashId !== "NO HANDS DETECTED") {
        this.ui.flashGesture(flashId);
        this.lastFlashed = flashId;
      }
    }
    if (driving) this.lastDrivingAt = now;
    else if (now - this.lastDrivingAt > GESTURE_NEUTRAL_MS) this.universe.settleToBaseline();
  }

  private toggleVisualTest(): void {
    if (this.visualTest.isActive()) {
      this.visualTest.stop();
      this.ui.setVisualTest(false);
      this.universe.setBlackHole(false);
      this.universe.settleToBaseline();
      return;
    }
    this.ui.hideLanding();
    this.visualTest.start();
    this.lastVisualStep = "";
    this.lastVisualFormationAt = 0;
    this.ui.setVisualTest(true, "VISUAL TEST MODE");
  }

  private stepVisualTest(now: number): void {
    const step = this.visualTest.current(now);
    if (!step) {
      this.ui.setVisualTest(false);
      this.universe.setBlackHole(false);
      this.universe.settleToBaseline();
      return;
    }
    this.ui.setVisualTest(true, step.label);
    if (step.id !== this.lastVisualStep) {
      this.lastVisualStep = step.id;
      this.ui.flashGesture(step.label.replace("VISUAL TEST · ", ""));
      if (step.id === "supernova") {
        this.universe.supernova();
        this.audio.supernova();
      }
    }
    if (step.id === "galaxy") this.universe.settleToBaseline();
    if (step.id === "orbit") {
      this.universe.orbit(0.25 + 0.45 * Math.sin(now / 700), 0.45);
    }
    if (step.id === "gravity") this.universe.compress(0.82);
    if (step.id === "expand") this.universe.expandCosmic(0.9);
    if (step.id === "black-hole") this.universe.setBlackHole(true);
    else this.universe.setBlackHole(false);
    if (step.id === "formations" && now - this.lastVisualFormationAt > 1100) {
      this.lastVisualFormationAt = now;
      const id = this.visualTest.nextFormation();
      this.universe.setFormation(id);
      this.ui.toastFormation(id);
    }
  }

  private syncLookForm(): void {
    const form = this.ui.calibrationForm;
    (form.elements.namedItem("exposure") as HTMLInputElement).value = String(
      Math.round(this.look.exposure * 100),
    );
    (form.elements.namedItem("bloomStrength") as HTMLInputElement).value = String(
      Math.round(this.look.bloomStrength * 100),
    );
    (form.elements.namedItem("bloomRadius") as HTMLInputElement).value = String(
      Math.round(this.look.bloomRadius * 100),
    );
    (form.elements.namedItem("bloomThreshold") as HTMLInputElement).value = String(
      Math.round(this.look.bloomThreshold * 100),
    );
    (form.elements.namedItem("pointSizeMult") as HTMLInputElement).value = String(
      Math.round(this.look.pointSizeMult * 100),
    );
    (form.elements.namedItem("cameraZ") as HTMLInputElement).value = String(
      Math.round(this.look.cameraZ * 10),
    );
  }

  private readLookForm(): void {
    const form = this.ui.calibrationForm;
    const data = new FormData(form);
    this.look = validateLook({
      exposure: Number(data.get("exposure")) / 100,
      bloomStrength: Number(data.get("bloomStrength")) / 100,
      bloomRadius: Number(data.get("bloomRadius")) / 100,
      bloomThreshold: Number(data.get("bloomThreshold")) / 100,
      pointSizeMult: Number(data.get("pointSizeMult")) / 100,
      cameraZ: Number(data.get("cameraZ")) / 10,
    });
    saveLook(this.look);
    this.universe.applyLook(this.look);
    const snap = this.universe.debugSnapshot();
    this.ui.calibrationStats.textContent = `cover ${(snap.coverage * 100).toFixed(0)}% · z ${snap.cameraZ.toFixed(1)} · bloom ${snap.bloom.toFixed(2)} · exp ${snap.exposure.toFixed(2)}`;
  }

  private resetVisuals(): void {
    this.universe.resetUniverse();
    this.gesture.reset();
    this.blackHoleLatched = false;
    this.lastDrivingAt = 0;
    this.lastGestureId = "none";
    this.lastFlashed = "";
    this.universe.applyLook(this.look);
  }

  private formatDebug(
    gesture: string,
    confidence: number,
    fps: string,
    extra?: {
      faces: number;
      faceMs: number;
      hands: number;
      handMs: number;
      fallback: boolean;
    },
  ): string {
    const snap = this.universe.debugSnapshot();
    const lines = [
      fps,
      `gesture: ${gesture}  conf: ${confidence.toFixed(2)}`,
      `cam z: ${snap.cameraZ.toFixed(2)}  scale: ${snap.scale.toFixed(2)}  radius: ${snap.radius.toFixed(2)}  cover: ${(snap.coverage * 100).toFixed(0)}%`,
      `energy: ${snap.energy.toFixed(2)}  size: ${snap.pointSize.toFixed(2)}  max: ${snap.maxPointSize}`,
      `bloom: ${snap.bloom.toFixed(2)}/${snap.bloomRadius.toFixed(2)}/${snap.bloomThreshold.toFixed(2)}  exp: ${snap.exposure.toFixed(2)}`,
      `formation: ${snap.formation}`,
    ];
    if (extra) {
      lines.push(
        `faces: ${extra.faces}  faceMs: ${extra.faceMs.toFixed(1)}  hands: ${extra.hands}  handMs: ${extra.handMs.toFixed(1)}`,
        `fallback: ${extra.fallback ? "YES" : "no"}`,
      );
    }
    return lines.join("\n");
  }

  private clearPreviewToBlack(): void {
    const out = this.ui.cameraCanvas;
    if (out.width < 2) {
      out.width = 640;
      out.height = 480;
    }
    this.previewCtx.fillStyle = "#000";
    this.previewCtx.fillRect(0, 0, out.width, out.height);
  }

  private composeRecording(): void {
    if (!this.recorder.recording && this.recorder.countdown <= 0 && !this.visualTest.isActive())
      return;
    if (!this.recorder.recording && this.recorder.countdown <= 0) return;
    const gl = this.universe.canvas;
    const width = gl.width || Math.round(gl.clientWidth);
    const height = gl.height || Math.round(gl.clientHeight);
    const dpr = width / Math.max(1, gl.clientWidth);
    const cam = this.ui.processedCanvasRect();
    this.recorder.compose({
      universe: gl,
      camera: this.ui.cameraCanvas,
      cameraRect: {
        x: cam.left * dpr,
        y: cam.top * dpr,
        width: cam.width * dpr,
        height: cam.height * dpr,
      },
      width,
      height,
      overlays: [
        (ctx, w) => {
          ctx.fillStyle = "rgba(232,246,255,0.95)";
          ctx.font = `700 ${Math.round(18 * dpr)}px Segoe UI, sans-serif`;
          ctx.fillText("GESTUREVERSE", 28 * dpr, 40 * dpr);
          ctx.font = `${Math.round(14 * dpr)}px Segoe UI, sans-serif`;
          ctx.fillStyle = "#00f0ff";
          ctx.fillText(
            "LOCAL PROCESSING  ·  NO VIDEO UPLOADED  ·  FACE CONCEALED",
            28 * dpr,
            62 * dpr,
          );
          ctx.fillStyle = "#e8f4ff";
          ctx.font = `700 ${Math.round(22 * dpr)}px Segoe UI, sans-serif`;
          ctx.fillText(this.ui.gestureLabel.textContent ?? "", 28 * dpr, 92 * dpr);
          const flash = this.ui.gestureFlash;
          const flashText =
            flash.querySelector(".gesture-flash-inner")?.textContent ?? flash.textContent;
          if (flash.classList.contains("is-on") && flashText) {
            ctx.textAlign = "center";
            ctx.font = `700 ${Math.round(56 * dpr)}px Segoe UI, sans-serif`;
            ctx.fillStyle = "#f4fbff";
            ctx.fillText(flashText, w / 2, height * 0.5);
            ctx.textAlign = "left";
          }
          if (this.recorder.recording) {
            const seconds = this.recorder.elapsedSeconds();
            const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
            const ss = String(Math.floor(seconds % 60)).padStart(2, "0");
            ctx.fillStyle = "#ff3b6b";
            ctx.beginPath();
            ctx.arc(w / 2 - 40 * dpr, 32 * dpr, 7 * dpr, 0, Math.PI * 2);
            ctx.fill();
            ctx.font = `600 ${Math.round(16 * dpr)}px Segoe UI, sans-serif`;
            ctx.fillText(`REC  ${mm}:${ss}`, w / 2 - 24 * dpr, 38 * dpr);
          }
        },
      ],
    });
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (this.cameraOn) return;
    const rect = this.universe.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    this.universe.mouseOrbit(x, y);
  };

  private onMouseDown = (): void => {
    if (this.cameraOn) return;
    this.lastDrivingAt = performance.now();
    this.universe.compress(0.75);
  };

  private onMouseUp = (): void => {
    if (this.cameraOn) return;
    this.universe.settleToBaseline();
  };

  private onKey = (event: KeyboardEvent): void => {
    const target = event.target;
    const typing =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement;
    if (event.key === "Escape") {
      this.ui.toggleSettings(false);
      this.ui.toggleCalibration(false);
      this.ui.hideError();
      return;
    }
    if (typing) return;
    if (event.code === "Space") {
      event.preventDefault();
      void this.toggleRecording();
    } else if (event.key === "r" || event.key === "R") {
      this.resetVisuals();
    } else if (event.key === "p" || event.key === "P") {
      this.settings.set({ presentationMode: !this.settings.get().presentationMode });
    } else if (event.key === "d" || event.key === "D") {
      this.settings.set({ debugMode: !this.settings.get().debugMode });
    } else if (event.key === "m" || event.key === "M") {
      void this.toggleSound();
    } else if (event.key >= "1" && event.key <= "6") {
      const id = FORMATION_ORDER[Number(event.key) - 1] as FormationId;
      this.universe.setFormation(id);
      this.ui.toastFormation(id);
      this.audio.formationSweep();
    } else if (event.key === "f" || event.key === "F") {
      this.universe.supernova();
      this.audio.supernova();
    } else if (event.key === "b" || event.key === "B") {
      this.universe.setBlackHole(true);
      this.audio.blackHole(true);
      window.setTimeout(() => this.universe.setBlackHole(false), 1800);
    } else if (event.key === "e" || event.key === "E") {
      this.lastDrivingAt = performance.now();
      this.universe.awaken();
    }
  };

  private onVisibility = (): void => {
    if (document.hidden && this.cameraOn) {
      this.ui.setCameraCover(true, "PRIVACY FALLBACK ACTIVE.");
    }
  };

  private resize = (): void => {
    this.universe.resize(window.innerWidth, window.innerHeight);
  };

  dispose = (): void => {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.camera.stop();
    this.hands.close();
    this.faces.close();
    this.privacy.reset();
    this.audio.dispose();
    this.recorder.dispose();
    this.universe.dispose();
    window.removeEventListener("resize", this.resize);
    window.removeEventListener("orientationchange", this.resize);
    window.removeEventListener("keydown", this.onKey);
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("pagehide", this.dispose);
    window.removeEventListener("beforeunload", this.dispose);
  };
}

function fpsLine(fps: number): string {
  return `fps: ${fps.toFixed(0)}`;
}
